<template>
  <CollapsibleRoot v-show="hasVisibleItems" v-model:open="isOpen" as-child>
    <li>
      <CollapsibleTrigger class="group flex w-full items-center justify-between py-3 text-button-xl text-text-strong data-[state=closed]:text-text md:hidden">
        <span class="flex items-center">
          <HallIcon
            class="text-icon-active"
            :class="{
              '-m-3 -mr-1 size-12': props.item.id,
              'mr-2 size-6': !props.item.id,
            }"
            :name="computedIcon"
          />
          {{ props.item.name }}
        </span>
        <Icon class="size-6 text-text transition-transform ease-in-out group-data-[state=closed]:rotate-180" name="chevron-up" />
      </CollapsibleTrigger>

      <CollapsibleContent class="overflow-hidden data-[state=closed]:animate-collapsible-close data-[state=open]:animate-collapsible-open md:hidden">
        <ul class="relative mb-3 rounded-lg bg-background-light-strong px-6">
          <li v-if="matchesSearchTerm(allLabel)" class="mb-3 border-b border-border-medium pb-4 text-base text-text-strong first:pt-5 last:border-b-0">
            <NuxtLink :to="props.item.url" class="hover:underline"><span v-html="highlightText(allLabel)" /></NuxtLink>
            <Icon class="ml-2 inline-block size-4" name="arrow-right" />
          </li>
          <template v-for="(childItem, childIndex) in getVisibleItems(props.item.children)" :key="childIndex">
            <li class="py-2 text-base first:pt-5 last:pb-5">
              <NuxtLink :to="childItem.url" class="hover:underline"><span v-html="highlightText(childItem.name)" /></NuxtLink>
            </li>
          </template>
        </ul>
      </CollapsibleContent>

      <div class="hidden md:block">
        <ul class="relative mb-3 rounded-lg px-6 pl-8">
          <li
            class="relative pb-2 text-base text-text"
            :class="{
              'text-link-active': !matchesSearchTerm(allLabel),
            }"
          >
            <HallIcon
              class="absolute -left-8 -top-1 text-icon-active"
              :class="{
                '-m-3 size-12': props.item.id,
                'size-6': !props.item.id,
              }"
              :name="computedIcon"
            />
            <NuxtLink
              :to="props.item.url"
              class="inline-block hover:underline"
              :class="{
                'font-medium': props.searchTerm.length < 3,
              }"
            >
              <span v-html="highlightText(props.item.name)" />
            </NuxtLink>
          </li>
          <template v-for="(childItem, childIndex) in getVisibleItems(props.item.children)" :key="childIndex">
            <li class="py-1 text-base first:pt-5 last:pb-5">
              <NuxtLink :to="childItem.url" class="hover:underline"><span v-html="highlightText(childItem.name)" /></NuxtLink>
            </li>
          </template>
        </ul>
      </div>
    </li>
  </CollapsibleRoot>
</template>

<script setup lang="ts">
import * as t from '~/messages'
import { CollapsibleRoot, CollapsibleTrigger, CollapsibleContent } from 'reka-ui'
import HallIcon from '~/components/icon/HallIcon.vue'

const isOpen = ref(false)

interface CategoryItem {
  id: number
  name: string
  url: string
  children: CategoryItemChild[]
  adopted: boolean
}

interface CategoryItemChild {
  id: number
  name: string
  url: string
  adopted: boolean
}

const props = withDefaults(
  defineProps<{
    item: CategoryItem
    accordionId?: string
    searchTerm?: string
    icon?: string
    allName?: string
  }>(),
  {
    accordionId: '',
    searchTerm: '',
    icon: '',
    allName: '',
  }
)

const allLabel = computed(() => props.allName || t['w.module.categories_search.view_all']().replace('%s', props.item.name.toLowerCase()))
const computedIcon = computed(() => props.icon || 'legacy-' + props.item.id)

const escapeRegExp = (string: string) => {
  return string.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&').replace(/-/g, '\\u002d')
}

const highlightText = (text: string) => {
  if (props.searchTerm?.length < 3) {
    return text
  }

  const ignoreCaseForSearchTerm = new RegExp(escapeRegExp(props.searchTerm), 'gi')

  return text.replace(ignoreCaseForSearchTerm, '<strong>$&</strong>')
}

const matchesSearchTerm = (text: string) => {
  if (props.searchTerm?.length < 3) {
    return true
  }

  const ignoreCaseForSearchTerm = new RegExp(escapeRegExp(props.searchTerm), 'gi')

  return text.match(ignoreCaseForSearchTerm) !== null
}

const getVisibleItems = (items: CategoryItemChild[]) => {
  return items.filter((item) => matchesSearchTerm(item.name))
}

const hasVisibleItems = computed(() => getVisibleItems(props.item.children)?.length > 0 || matchesSearchTerm(allLabel.value))

const closeAccordion = () => {
  isOpen.value = false
}

const openAccordion = () => {
  isOpen.value = true
}

defineExpose({
  closeAccordion,
  openAccordion,
  hasVisibleItems,
})
</script>
